import React, { useEffect, useRef } from 'react';
import { Label as LabelIcon } from '@material-ui/icons';
import Card from '../../../common/card/Card';
import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineSelect from '../../../common/inline/InlineSelect';
import InlineHtml from '../../../common/inline/InlineHtml';
import { useUpdateChangeOrder } from '../ChangeOrder.hook';
import {
  BaseChangeOrderCardProps,
  ChangeOrder
} from '../ChangeOrder.interface';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import InlineDatePicker from '../../../common/inline/InlineDatePicker';

export interface ChangeOrderLabelingCardProps extends BaseChangeOrderCardProps {
  changeOrder: ChangeOrder;
  setSuccessMessage: (message: string) => void;
  setErrorMessage: (message: string) => void;
}

const LabelingCardFields = [
  { name: 'labelingReview', label: 'Legal review of labeling' },
  {
    name: 'regulatoryRegistrationAffected',
    label: 'Impact to labeling that has been submitted to regulatory agencies?'
  },
  { name: 'claimsAffected', label: 'Claims' },
  { name: 'udiAffected', label: 'UDI affected' },
  { name: 'printedLabelImpact', label: 'Impact on preprinted labels?' },
  {
    name: 'labelProgramUpdated',
    label: 'Label program updated in manufacturing?'
  },
  {
    name: 'testLabelAttached',
    label:
      'For in house label printing only, Test label printed and attached to change order?'
  }
];

export const ChangeOrderLabelingCard: React.FC<
  ChangeOrderLabelingCardProps
> = props => {
  const { changeOrder, onChange, setSuccessMessage, setErrorMessage } = props;
  const onChangeRef = useRef(onChange);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(changeOrder.task.id || 0);
  const { updatedChangeOrder, isLoading, error, updateChangeOrder } =
    useUpdateChangeOrder(changeOrder.id);
  const { isFieldDisabled } = useTaskEntityFields(changeOrder.task);

  const initialValues: Partial<ChangeOrder> = {
    labelingReview: changeOrder.labelingReview || '',
    regulatoryRegistrationAffected:
      changeOrder.regulatoryRegistrationAffected || '',
    claimsAffected: changeOrder.claimsAffected || '',
    udiAffected: changeOrder.udiAffected || '',
    printedLabelImpact: changeOrder.printedLabelImpact || '',
    labelProgramUpdated: changeOrder.labelProgramUpdated || '',
    testLabelAttached: changeOrder.testLabelAttached || '',
    commentsLabeling: changeOrder.commentsLabeling || '',
    lblDueDate: changeOrder.lblDueDate,
    lblCompletedDate: changeOrder.lblCompletedDate
  };

  useEffect(() => {
    if (updatedChangeOrder?.id) {
      setSuccessMessage('Change Order successfully updated');
      onChangeRef.current?.(updatedChangeOrder);
    }
  }, [setSuccessMessage, updatedChangeOrder]);

  useEffect(() => {
    if (updatedTask) {
      changeOrder.task = Object.assign(changeOrder.task || {}, updatedTask);
      onChange?.(changeOrder);
    }
  }, [updatedTask]);

  useEffect(() => {
    if (Boolean(error || updateTaskError))
      setErrorMessage('There was an error saving');
  }, [setErrorMessage, error, updateTaskError]);
  return (
    <InlineFormik
      initialValues={initialValues}
      strict={false}
      isSubmitting={isLoading}
      error={error}
      onSubmit={(updatedValue: Partial<ChangeOrder>) => {
        const dto = { ...updatedValue };
        updateChangeOrder(dto);
      }}
    >
      <Card>
        <CardTitle>
          <LabelIcon />
          Labeling (LBL) Assessment
        </CardTitle>
        <CardContent padding='large'>
          <InlineForm>
            {LabelingCardFields.map((field, key) => (
              <InlineSelect
                key={key}
                name={field.name}
                label={field.label}
                disabled={isFieldDisabled(field.name)}
                options={[
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' }
                ]}
              />
            ))}
            <InlineDatePicker
              grid={{ lg: 6, md: 6 }}
              clearable
              name='lblDueDate'
              label='Due Date'
              placeholder='Enter Due Date'
              disabled={isFieldDisabled('lblDueDate')}
              autoCloseOnSelect
            />
            <InlineDatePicker
              grid={{ lg: 6, md: 6 }}
              clearable
              name='lblCompletedDate'
              label='Completed Date'
              placeholder='Enter Completed Date'
              disabled={isFieldDisabled('lblCompletedDate')}
              autoCloseOnSelect
            />
            <InlineHtml
              name='commentsLabeling'
              label='Comments'
              disabled={isFieldDisabled('commentsLabeling')}
              helperText='Provide any additional information here'
              attachments={changeOrder.task.attachments || []}
              onChangeAttachments={attachments => {
                updateTask({ attachments });
                if (changeOrder.task) {
                  changeOrder.task.attachments = attachments;
                }
                onChange?.(changeOrder);
              }}
            />
          </InlineForm>
        </CardContent>
      </Card>
    </InlineFormik>
  );
};
